import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * IconButton component props
 */
export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  /**
   * The icon to render
   */
  icon: ReactNode;
  /**
   * Accessible name (required - the button has no visible text).
   * Also used as the tooltip unless `title` is given.
   */
  label: string;
  /**
   * Content shown instead of the icon while busy (e.g. "...")
   */
  busyContent?: ReactNode;
  /**
   * Whether the action is in progress (disables the button)
   */
  isBusy?: boolean;
}

/**
 * Icon-only button. `Button` requires text children, so row actions used raw
 * `<button>`s; this primitive guarantees `type="button"` and an accessible label.
 * Visual style comes from the page class passed in `className`.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { icon, label, title, busyContent, isBusy = false, disabled, className = '', type = 'button', ...props },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        className={['icon-btn', className].filter(Boolean).join(' ')}
        aria-label={label}
        title={title ?? label}
        disabled={disabled || isBusy}
        aria-busy={isBusy || undefined}
        {...props}
      >
        {isBusy && busyContent !== undefined ? busyContent : icon}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
